import React, { useState, useMemo, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { useAuthContext } from '../../context/auth-context';
import { useGetAllAffiliationsQuery } from '../../redux/services/affiliation-api';
import { useImportClubsMutation } from '../../redux/services/club-api';

const PRIMARY = '#0F6E56';
const IGNORE_VALUE = '__ignore__';

const STEPS = ['Subir archivo', 'Mapear columnas', 'Confirmar'];

const FIELD_ALIASES = {
  name: ['name', 'nombre', 'club', 'nombre del club', 'nombreclub'],
  director: ['director', 'lider', 'líder', 'leader', 'responsable'],
  affiliatedTo: ['affiliatedto', 'affiliated_to', 'zona', 'zone', 'afiliacion', 'afiliación', 'afiliado', 'afiliado a'],
  status: ['status', 'estado', 'active', 'activo'],
};

function autoDetect(headers) {
  const map = { name: IGNORE_VALUE, director: IGNORE_VALUE, affiliatedTo: IGNORE_VALUE, status: IGNORE_VALUE };
  headers.forEach(h => {
    const norm = h.toLowerCase().trim().replace(/\s+/g, '');
    Object.entries(FIELD_ALIASES).forEach(([field, aliases]) => {
      if (map[field] === IGNORE_VALUE) {
        if (aliases.some(a => norm === a || norm.includes(a) || a.includes(norm))) {
          map[field] = h;
        }
      }
    });
  });
  return map;
}

function resolveAffiliation(value, affiliations) {
  if (!value) return null;
  const v = String(value).trim().toLowerCase();
  const byZone = affiliations.find(a => a.zone?.toLowerCase().trim() === v);
  if (byZone) return byZone._id;
  const byName = affiliations.find(a => a.name?.toLowerCase().trim() === v);
  if (byName) return byName._id;
  const byPartial = affiliations.find(a =>
    (a.zone && v.includes(a.zone.toLowerCase().trim())) ||
    (a.zone && a.zone.toLowerCase().trim().includes(v))
  );
  if (byPartial) return byPartial._id;
  return null;
}

function parseStatus(value) {
  if (value === undefined || value === null || value === '') return true;
  const v = String(value).toLowerCase().trim();
  return ['true', '1', 'activo', 'active', 'yes', 'sí', 'si'].includes(v);
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Step sidebar ─────────────────────────────────────────────────────────────

function StepSidebar({ step }) {
  return (
    <Box
      sx={{
        width: 200,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        py: 3,
        px: 2,
        display: { xs: 'none', sm: 'block' },
      }}
    >
      {STEPS.map((label, i) => {
        const done = i < step;
        const active = i === step;
        return (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1.25,
              px: 1,
              borderRadius: 1,
              mb: 0.5,
              bgcolor: active ? `${PRIMARY}14` : 'transparent',
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: done ? PRIMARY : active ? PRIMARY : 'transparent',
                border: done || active ? 'none' : '1.5px solid',
                borderColor: 'divider',
                color: done || active ? 'white' : 'text.disabled',
                fontSize: '0.7rem',
                fontWeight: 700,
              }}
            >
              {done ? '✓' : i + 1}
            </Box>
            <Typography
              variant="body2"
              fontWeight={active ? 600 : 400}
              color={active ? PRIMARY : done ? 'text.primary' : 'text.disabled'}
            >
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Step 0: File upload ──────────────────────────────────────────────────────

function StepUpload({ fileInfo, onFile, onRemove, affiliations }) {
  const [dragging, setDragging] = useState(false);
  const [parseError, setParseError] = useState('');
  const inputRef = useRef(null);

  const handleFile = useCallback(async file => {
    setParseError('');
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setParseError('Formato no soportado. Usa .xlsx, .xls o .csv');
      return;
    }
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      const headers = (data[0] || []).map(String).filter(h => h.trim());
      const rows = data.slice(1).filter(r => r.some(c => String(c).trim() !== ''));
      if (headers.length === 0) {
        setParseError('El archivo está vacío o no tiene encabezados.');
        return;
      }
      if (rows.length > 1000) {
        setParseError(''); // allow but warn in UI
      }
      onFile({ name: file.name, size: file.size, headers, rows });
    } catch {
      setParseError('No se pudo leer el archivo. Verifica que no esté dañado.');
    }
  }, [onFile]);

  const handleDrop = e => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const downloadTemplate = () => {
    const headerRow = ['name', 'director', 'affiliatedTo'];
    const sampleZone = affiliations[0]?.zone || affiliations[0]?.name || 'Zona 01';
    const exampleRow = ['Club Ejemplo', 'Juan Pérez', sampleZone];
    const ws = XLSX.utils.aoa_to_sheet([headerRow, exampleRow]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clubes');
    XLSX.writeFile(wb, 'plantilla-clubes.xlsx');
  };

  if (fileInfo) {
    return (
      <Box>
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderRadius: 2,
            borderColor: PRIMARY,
          }}
        >
          <InsertDriveFileIcon sx={{ color: PRIMARY, fontSize: 36 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" fontWeight={500} noWrap>{fileInfo.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatBytes(fileInfo.size)} · {fileInfo.rows.length} filas · {fileInfo.headers.length} columnas
            </Typography>
          </Box>
          <IconButton size="small" onClick={onRemove}><CloseIcon fontSize="small" /></IconButton>
        </Paper>
        {fileInfo.rows.length > 1000 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            El archivo tiene {fileInfo.rows.length} filas. La importación puede tardar unos segundos.
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Box
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: dragging ? PRIMARY : 'divider',
          borderRadius: 2,
          p: 5,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: dragging ? `${PRIMARY}08` : 'background.paper',
          transition: 'all 0.15s',
          '&:hover': { borderColor: PRIMARY, bgcolor: `${PRIMARY}08` },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files[0])}
        />
        <UploadFileIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body1" fontWeight={500} gutterBottom>
          Arrastra tu archivo aquí o haz clic para seleccionar
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Formatos aceptados: .xlsx, .xls, .csv
        </Typography>
      </Box>

      {parseError && (
        <Alert severity="error" sx={{ mt: 2 }}>{parseError}</Alert>
      )}

      <Button
        size="small"
        startIcon={<FileDownloadIcon />}
        onClick={downloadTemplate}
        sx={{ mt: 2, textTransform: 'none', color: 'text.secondary' }}
      >
        Descargar plantilla Excel
      </Button>
    </Box>
  );
}

// ─── Step 1: Column mapping ───────────────────────────────────────────────────

const SYSTEM_FIELDS = [
  { key: 'name', label: 'Nombre', required: true },
  { key: 'director', label: 'Director', required: false },
  { key: 'affiliatedTo', label: 'Zona (Afiliado A)', required: true },
  { key: 'status', label: 'Estado', required: false, defaultLabel: 'Activo' },
];

function MappingBadge({ value, isAuto, required }) {
  if (value === IGNORE_VALUE) {
    if (required) return <Chip label="⚠ Requerido" size="small" sx={{ bgcolor: '#fff8e1', color: '#f57c00', fontWeight: 500 }} />;
    return <Chip label="Default: Activo" size="small" sx={{ bgcolor: '#f5f5f5', color: '#777', fontWeight: 500 }} />;
  }
  if (isAuto) return <Chip label="✓ Auto" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }} />;
  return <Chip label="✓ Asignado" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 500 }} />;
}

function StepMapping({ headers, rows, colMap, onColMapChange, autoDetected }) {
  const getPreview = col => {
    if (!col || col === IGNORE_VALUE) return ['—', '—'];
    const idx = headers.indexOf(col);
    if (idx < 0) return ['—', '—'];
    return rows.slice(0, 2).map(r => String(r[idx] ?? '').trim() || '—');
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Indica qué columna de tu archivo corresponde a cada campo del sistema.
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: 180 }}>Campo del sistema</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: 200 }}>Columna en tu archivo</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Vista previa</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: 130 }}>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SYSTEM_FIELDS.map(field => {
              const selected = colMap[field.key];
              const preview = getPreview(selected);
              const isAuto = autoDetected[field.key] === selected && selected !== IGNORE_VALUE;
              return (
                <TableRow key={field.key}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {field.label}
                      {field.required && <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select
                        value={selected}
                        onChange={e => onColMapChange(field.key, e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value={IGNORE_VALUE}>— Ignorar —</MenuItem>
                        {headers.map(h => (
                          <MenuItem key={h} value={h}>{h}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    {selected !== IGNORE_VALUE ? (
                      <Box>
                        {preview.map((v, i) => (
                          <Typography key={i} variant="caption" display="block" color="text.secondary" noWrap>
                            {v}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <MappingBadge value={selected} isAuto={isAuto} required={field.required} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        Los clubes sin director se importarán con ese campo vacío. Podrás editarlos después desde la tabla.
      </Typography>
    </Box>
  );
}

// ─── Step 2: Confirm ──────────────────────────────────────────────────────────

function StatCard({ value, label, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', flex: 1, borderRadius: 1 }}>
      <Typography variant="h4" fontWeight={700} color={color || 'text.primary'}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

function StepConfirm({ mappedRows, affiliations }) {
  const withoutDirector = mappedRows.filter(r => !r.director).length;
  const uniqueZones = new Set(mappedRows.map(r => r.affiliatedToRaw).filter(Boolean)).size;
  const errorRows = mappedRows.filter(r => r.hasError);
  const preview = mappedRows.slice(0, 5);

  const getZoneLabel = affiliationId => {
    if (!affiliationId) return '—';
    const aff = affiliations.find(a => a._id === affiliationId);
    return aff ? (aff.zone || aff.name) : '—';
  };

  return (
    <Box>
      {/* Stat cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <StatCard value={mappedRows.length} label="clubes a importar" color={PRIMARY} />
        <StatCard value={withoutDirector} label="sin director" />
        <StatCard value={uniqueZones} label="zonas detectadas" />
      </Box>

      {/* Error warning */}
      {errorRows.length > 0 && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ mb: 2 }}
        >
          {errorRows.length} {errorRows.length === 1 ? 'fila tiene' : 'filas tienen'} posibles errores (zona no reconocida o nombre vacío).
          Se importarán igual — podrás editarlas después.
        </Alert>
      )}

      {/* Preview table */}
      <Typography variant="subtitle2" fontWeight={600} mb={1}>
        Vista previa (primeras 5 filas)
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Director</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Zona</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Estado</TableCell>
              <TableCell sx={{ width: 32 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {preview.map((row, i) => (
              <TableRow
                key={i}
                sx={{ bgcolor: row.hasError ? '#fffde7' : undefined }}
              >
                <TableCell>
                  <Typography variant="body2">{row.name || <Box component="span" color="error.main">Vacío</Box>}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={row.director ? 'text.primary' : 'text.disabled'}>
                    {row.director || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {row.affiliatedTo ? (
                    <Typography variant="body2">{getZoneLabel(row.affiliatedTo)}</Typography>
                  ) : (
                    <Typography variant="body2" color="warning.main">
                      {row.affiliatedToRaw ? `"${row.affiliatedToRaw}" — no reconocida` : '—'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.status ? 'Activo' : 'Inactivo'}</Typography>
                </TableCell>
                <TableCell>
                  {row.hasError && <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                  {!row.hasError && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Main wizard ──────────────────────────────────────────────────────────────

export default function ClubImportWizard() {
  const navigate = useNavigate();
  const { activeCamp } = useAuthContext();
  const [importClubs] = useImportClubsMutation();

  const { data: affiliations = [] } = useGetAllAffiliationsQuery(
    activeCamp?._id ? { campId: activeCamp._id } : undefined,
    { skip: !activeCamp?._id }
  );

  const [step, setStep] = useState(0);
  const [fileInfo, setFileInfo] = useState(null); // { name, size, headers, rows }
  const [colMap, setColMap] = useState({ name: IGNORE_VALUE, director: IGNORE_VALUE, affiliatedTo: IGNORE_VALUE, status: IGNORE_VALUE });
  const [autoDetected, setAutoDetected] = useState({});
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState(null);

  const handleFile = useCallback(info => {
    const detected = autoDetect(info.headers);
    setFileInfo(info);
    setColMap(detected);
    setAutoDetected(detected);
  }, []);

  const handleColMapChange = (field, value) => {
    setColMap(prev => ({ ...prev, [field]: value }));
  };

  const mappedRows = useMemo(() => {
    if (!fileInfo) return [];
    const { headers, rows } = fileInfo;
    return rows.map(row => {
      const get = key => {
        const col = colMap[key];
        if (!col || col === IGNORE_VALUE) return '';
        const idx = headers.indexOf(col);
        return idx >= 0 ? String(row[idx] ?? '').trim() : '';
      };
      const rawZone = get('affiliatedTo');
      const affiliationId = resolveAffiliation(rawZone, affiliations);
      const status = parseStatus(get('status'));
      const name = get('name');
      return {
        name,
        director: get('director'),
        affiliatedTo: affiliationId,
        affiliatedToRaw: rawZone,
        status,
        hasError: !name || (!affiliationId && Boolean(rawZone)),
      };
    }).filter(r => r.name);
  }, [fileInfo, colMap, affiliations]);

  const canProceed = () => {
    if (step === 0) return Boolean(fileInfo);
    if (step === 1) return colMap.name !== IGNORE_VALUE && colMap.affiliatedTo !== IGNORE_VALUE;
    return true;
  };

  const handleNext = async () => {
    if (step < 2) {
      setStep(s => s + 1);
      return;
    }
    // Step 2: import
    setImporting(true);
    setImportError('');
    const payload = mappedRows.map(r => ({
      name: r.name,
      director: r.director || undefined,
      affiliatedTo: r.affiliatedTo || undefined,
      status: r.status,
    }));
    const result = await importClubs({ clubs: payload, campId: activeCamp?._id });
    setImporting(false);
    if (result.error) {
      setImportError('Error al importar. Intenta de nuevo.');
      return;
    }
    const count = result.data?.imported ?? mappedRows.length;
    setImportSuccess(count);
    setTimeout(() => navigate('/clubes'), 1800);
  };

  const handleBack = () => {
    if (step > 0) setStep(s => s - 1);
    else navigate('/clubes');
  };

  if (importSuccess !== null) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: PRIMARY }} />
        <Typography variant="h5" fontWeight={700}>{importSuccess} clubes importados</Typography>
        <Typography variant="body2" color="text.secondary">Redirigiendo a la lista de clubes…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      {/* Topbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <IconButton size="small" onClick={() => navigate('/clubes')}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
          Importar clubes
        </Typography>
        <Button
          size="small"
          startIcon={<CloseIcon />}
          onClick={() => navigate('/clubes')}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Cancelar
        </Button>
      </Box>

      {/* Body */}
      <Paper variant="outlined" sx={{ flex: 1, display: 'flex', overflow: 'hidden', borderRadius: 1 }}>
        <StepSidebar step={step} />

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Step content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
            {step === 0 && (
              <StepUpload
                fileInfo={fileInfo}
                onFile={handleFile}
                onRemove={() => { setFileInfo(null); setColMap({ name: IGNORE_VALUE, director: IGNORE_VALUE, affiliatedTo: IGNORE_VALUE, status: IGNORE_VALUE }); }}
                affiliations={affiliations}
              />
            )}
            {step === 1 && fileInfo && (
              <StepMapping
                headers={fileInfo.headers}
                rows={fileInfo.rows}
                colMap={colMap}
                onColMapChange={handleColMapChange}
                autoDetected={autoDetected}
              />
            )}
            {step === 2 && (
              <StepConfirm mappedRows={mappedRows} affiliations={affiliations} />
            )}
          </Box>

          <Divider />

          {/* Footer */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              bgcolor: '#fafafa',
            }}
          >
            <Button
              size="small"
              onClick={handleBack}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              {step === 0 ? '← Volver a clubes' : '← Atrás'}
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {importError && (
                <Typography variant="caption" color="error">{importError}</Typography>
              )}
              {step === 1 && (colMap.name === IGNORE_VALUE || colMap.affiliatedTo === IGNORE_VALUE) && (
                <Typography variant="caption" color="text.secondary">
                  Asigna Nombre y Zona para continuar
                </Typography>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={handleNext}
                disabled={!canProceed() || importing}
                sx={{
                  bgcolor: PRIMARY,
                  '&:hover': { bgcolor: '#0a5a45' },
                  textTransform: 'none',
                  minWidth: 140,
                }}
              >
                {importing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : step === 2 ? (
                  `Importar ${mappedRows.length} club${mappedRows.length === 1 ? '' : 'es'}`
                ) : (
                  'Continuar →'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
